import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import {
  WorkflowIcon,
  SettingsIcon,
  LockIcon,
  MenuIcon,
  ChevronsUpDown,
  ExternalLinkIcon,
  LifeBuoy,
  Volume2,
  VolumeX,
} from "lucide-react";

import { classNames } from "../../utils/functions";
import { useAuth } from "../../contexts/AuthContext";
import { useTenant } from "../../contexts/TenantContext";
import { useAudio } from "../../contexts/AudioContext";

const initialNavigation = [
  {
    name: "Workflow definitions",
    href: "/workflows",
    icon: WorkflowIcon,
    current: false,
  },

  {
    name: "Your automations",
    href: "/",
    icon: SettingsIcon,
    current: false,
  },
  {
    name: "Credentials",
    href: "/credentials",
    icon: LockIcon,
    current: false,
  },
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useTenant();
  const { isAdmin, supabase } = useAuth();
  const { muted, setMuted } = useAudio();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const filteredNavigation = isAdmin
    ? initialNavigation
    : initialNavigation.filter((item) =>
        ["Delivery orders", "Your automations"].includes(item.name)
      );
  const [navigation, setNavigation] = useState(filteredNavigation);
  const [isCompressed, setIsCompressed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const updatedNavigation = navigation.map((item) => ({
      ...item,
      current: item.href === location.pathname,
    }));
    setNavigation(updatedNavigation);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNavigation = (href) => {
    navigate(href);
    setSidebarOpen(false);
  };

  const toggleCompression = () => setIsCompressed(!isCompressed);

  const renderSidebar = (isMobileSidebar = false) => (
    <div
      className={classNames(
        "flex h-full flex-col gap-y-5  overflow-y-auto bg-slate-50",
        "pb-4"
      )}
    >
      <div
        className={`flex h-14 px-4 border-b border-slate-200 shrink-0 items-center ${
          isCompressed && !isMobileSidebar
            ? "justify-center"
            : "justify-between"
        }`}
      >
        {(!isCompressed || isMobileSidebar) && (
          <button onClick={() => handleNavigation("/workflows")}>
            <img
              style={{ width: 30 }}
              src="./logomark.png"
              alt="Rectangle company logo"
            />
          </button>
        )}
        <div
          style={{ fontSize: 12 }}
          className="bg-white pl-4 pr-3 flex border border-slate-200 items-center space-x-2 py-2 text-xs text-slate-700 font-medium rounded-md"
        >
          <p>
            {tenant.charAt(0).toUpperCase() + tenant.slice(1).toLowerCase()}
          </p>
          <ChevronsUpDown className="opacity-40" strokeWidth={2.25} size={12} />
        </div>
      </div>

      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-7 px-2 ">
          <li>
            <ul className=" space-y-1 text-xs">
              {navigation.map((item) => (
                <li key={item.name}>
                  <button
                    onClick={() => handleNavigation(item.href)}
                    className={classNames(
                      item.current
                        ? "bg-white  border px-2 border-slate-200 text-gray-600"
                        : "text-gray-700 px-2 hover:bg-white hover:text-gray-600",
                      "group flex w-full gap-x-3 items-center rounded-md p-2 text-xs font-semibold leading-6",
                      isCompressed && !isMobileSidebar ? "justify-center" : ""
                    )}
                  >
                    <item.icon
                      aria-hidden="true"
                      className={classNames(
                        item.current
                          ? "text-gray-600 ml-1"
                          : "text-gray-400 group-hover:text-gray-600",
                        "h-4 w-4 shrink-0"
                      )}
                    />
                    {(!isCompressed || isMobileSidebar) && (
                      <span>{item.name}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </li>
        </ul>
      </nav>
    </div>
  );

  return (
    <main className="h-full">
      <div>
        {/* mobile sidebar */}
        <Dialog
          as="div"
          open={sidebarOpen}
          onClose={setSidebarOpen}
          className="relative z-50 lg:hidden"
        >
          <DialogBackdrop className="fixed inset-0 bg-gray-900/80" />

          <div className="fixed inset-0 flex">
            <DialogPanel className="relative mr-16 flex w-3/4 flex-1">
              <TransitionChild
                enter="transform transition ease-in-out duration-300"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                {renderSidebar(true)}
              </TransitionChild>
            </DialogPanel>
          </div>
        </Dialog>

        {/* desktop sidebar */}
        <div
          className={classNames(
            "fixed inset-y-0 z-50 border-r border-slate-200 flex flex-col transition-all bg-gray-50 duration-300",
            isCompressed ? "w-20" : "w-72",
            isMobile ? "hidden" : "flex"
          )}
        >
          {renderSidebar()}
          <div className="border-t text-xs border-slate-200 pb-6 pt-4 mx-4">
            <div key="sound-toggle">
              <button
                onClick={() => setMuted(!muted)}
                className={classNames(
                  "text-slate-500 hover:bg-white hover:text-gray-600",
                  "group flex  gap-x-3 items-center rounded-md p-2 text-xs font-semibold leading-6"
                )}
              >
                {muted ? (
                  <VolumeX
                    aria-hidden="true"
                    className="w-3 h-3 text-slate-500 group-hover:text-gray-600"
                  />
                ) : (
                  <Volume2
                    aria-hidden="true"
                    className="w-3 h-3 text-slate-500 group-hover:text-gray-600"
                  />
                )}
              </button>
            </div>
            <div key={"a"}>
              <button
                onClick={() => handleNavigation("#")}
                className={classNames(
                  "text-slate-500 hover:bg-white hover:text-gray-600",
                  "group flex w-full gap-x-3 items-center rounded-md p-2 text-xs font-semibold leading-6"
                )}
              >
                <ExternalLinkIcon
                  aria-hidden="true"
                  className={"w-3 h-3 text-slate-500 group-hover:text-gray-600"}
                />

                <span onClick={() => supabase.auth.signOut()}>Sign out</span>
              </button>
            </div>

            <div key={"a"}>
              <button
                onClick={() => handleNavigation("#")}
                className={classNames(
                  "text-slate-500 hover:bg-white hover:text-gray-600",
                  "group flex w-full gap-x-3 items-center rounded-md pt-2 px-2 text-xs font-semibold leading-6"
                )}
              >
                <LifeBuoy
                  strokeWidth={2.25}
                  aria-hidden="true"
                  className={"w-3 h-3 text-slae-500 group-hover:text-gray-600"}
                />

                <span>Support</span>
              </button>
            </div>
          </div>
        </div>

        {/* mobile menu button */}
        <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>

            <MenuIcon className="h-6 w-6" aria-hidden="true" />
          </button>

          <div className="flex-1 text-sm font-semibold leading-6 text-gray-900">
            {navigation.find((item) => item.current)?.name || "Dashboard"}
          </div>
        </div>

        {/* where all of the content will render */}
        <div
          className={classNames(
            "transition-all duration-300 ease-in-out",
            isMobile ? "" : isCompressed ? "lg:pl-20" : "lg:pl-72"
          )}
        >
          <main className="">
            <div className="">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </main>
  );
}
